import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Loader2, Plus, Layers, Rocket } from 'lucide-react';
import { useAdminAddons, type AdminAddon } from '@/hooks/useAdminCatalog';

const BRAND_LABELS: Record<string, string> = {
  saint_gobain: 'Saint-Gobain',
  knauf: 'Knauf',
  fassa: 'Fassa Bortolo',
};

export default function AdminStratigrafie() {
  const { data, isLoading } = useAdminAddons();

  if (isLoading) {
    return <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-zinc-400" /></div>;
  }
  if (!data) return null;

  // Solo Stratigrafie (category=stratigraphies) + Bundle, raggruppati per brand
  const strat = data.filter((a) => a.category === 'stratigraphies');
  const bundles = data.filter((a) => a.category === 'bundle');
  const byBrand = [...strat, ...bundles].reduce<Record<string, AdminAddon[]>>((acc, a) => {
    const k = a.brand_slug ?? 'altro';
    (acc[k] ??= []).push(a);
    return acc;
  }, {});

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-zinc-900">Stratigrafie & Bundle per brand</h1>
          <p className="text-sm text-zinc-500">Stratigrafie vendibili + bundle che includono listino + strat.</p>
        </div>
        <Button asChild>
          <Link to="/admin/releases"><Rocket className="h-4 w-4 mr-1" />Vai a Release per pubblicare</Link>
        </Button>
      </div>

      {Object.entries(byBrand).map(([brand, items]) => (
        <Card key={brand}>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Layers className="h-4 w-4 text-purple-600" />
              {BRAND_LABELS[brand] ?? brand}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {items.sort((a, b) => (a.category === 'bundle' ? 1 : -1)).map((item) => (
              <div key={item.id} className="flex items-center justify-between gap-3 px-3 py-2 rounded border border-zinc-200">
                <div className="flex items-center gap-2 min-w-0">
                  <Badge variant="outline" className={`text-[10px] ${item.category === 'bundle' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-purple-50 text-purple-700 border-purple-200'}`}>
                    {item.category === 'bundle' ? 'Bundle' : 'Strat'}
                  </Badge>
                  <span className="text-sm font-semibold text-zinc-900 truncate">{item.name}</span>
                  <Badge variant="outline" className="text-[10px] bg-zinc-50">v{item.version_year ?? '?'}</Badge>
                </div>
                <div className="flex items-center gap-4 text-xs">
                  {item.preview_count_stratigraphies > 0 && (
                    <span className="text-zinc-500">{item.preview_count_stratigraphies} strat</span>
                  )}
                  {item.preview_count_materials > 0 && (
                    <span className="text-zinc-500">{item.preview_count_materials.toLocaleString('it-IT')}+ mat</span>
                  )}
                  <span className="font-mono text-zinc-900">€{Number(item.price_eur).toFixed(0)}</span>
                </div>
              </div>
            ))}
            <div className="text-xs text-zinc-400 mt-2 flex items-center gap-1">
              <Plus className="h-3 w-3" />
              Una nuova versione di un brand crea automaticamente nuove righe Listino/Strat/Bundle. Vai su Listini → "Nuova versione".
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
